import React, { useState, useEffect, useRef } from 'react';
import { TextField, Box, Typography } from '@mui/material';

interface DateRangeInputProps {
  field: string;
  value: string | null; // Format: "2020-01-01-2021-01-02"
  onChange: (value: string | null) => void;
  placeholder?: string;
}

const ISO_DATE_RANGE_PATTERN = /^(\d{4}-\d{2}-\d{2})-(\d{4}-\d{2}-\d{2})$/;
const dateInputSx = {
  flex: 1,
  '& .MuiInputBase-input': {
    cursor: 'pointer',
    fontSize: '0.8125rem',
    px: 0.75,
    py: 0.75,
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.75rem',
    fontWeight: 600,
  },
} as const;

const hiddenDateInputStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
};

const formatDateForDisplay = (dateString: string) => {
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return '';
  }

  return `${match[3]}.${match[2]}.${match[1].slice(2)}.`;
};

const openDatePicker = (input: HTMLInputElement | null) => {
  if (!input) {
    return;
  }

  try {
    if (input.showPicker) {
      input.showPicker();
      return;
    }
  } catch {
    // Fall back to focus/click for browsers that do not allow showPicker here.
  }

  input.focus();
  input.click();
};

export const DateRangeInput: React.FC<DateRangeInputProps> = ({
  field,
  value,
  onChange,
  placeholder
}) => {
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const fromPickerRef = useRef<HTMLInputElement | null>(null);
  const toPickerRef = useRef<HTMLInputElement | null>(null);

  // Parse the value when it changes from outside
  useEffect(() => {
    const match = value?.match(ISO_DATE_RANGE_PATTERN);
    if (match) {
      setFromDate(match[1]);
      setToDate(match[2]);
    } else {
      setFromDate('');
      setToDate('');
    }
  }, [value]);

  const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFromDate = event.target.value;
    setFromDate(newFromDate);
    updateValue(newFromDate, toDate);
  };

  const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newToDate = event.target.value;
    setToDate(newToDate);
    updateValue(fromDate, newToDate);
  };

  const updateValue = (from: string, to: string) => {
    if (from && to) {
      onChange(`${from}-${to}`);
    } else if (!from && !to) {
      onChange(null);
    }
    // Don't update if only one date is provided
  };

  const label = placeholder || field;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.25,
          color: 'text.secondary',
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Box sx={{ position: 'relative', flex: 1 }}>
          <TextField
            size="small"
            label="From"
            value={formatDateForDisplay(fromDate)}
            onClick={() => openDatePicker(fromPickerRef.current)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ readOnly: true }}
            sx={dateInputSx}
            fullWidth
          />
          <input
            ref={fromPickerRef}
            aria-hidden="true"
            tabIndex={-1}
            style={hiddenDateInputStyle}
            type="date"
            value={fromDate}
            onChange={handleFromDateChange}
          />
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1 }}>
          -
        </Typography>
        <Box sx={{ position: 'relative', flex: 1 }}>
          <TextField
            size="small"
            label="To"
            value={formatDateForDisplay(toDate)}
            onClick={() => openDatePicker(toPickerRef.current)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ readOnly: true }}
            sx={dateInputSx}
            fullWidth
          />
          <input
            ref={toPickerRef}
            aria-hidden="true"
            tabIndex={-1}
            style={hiddenDateInputStyle}
            type="date"
            value={toDate}
            onChange={handleToDateChange}
          />
        </Box>
      </Box>
    </Box>
  );
};
